import { useState } from 'react';
import styles from './TransactionUpload.module.css';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    IconButton,
    Paper,
    Tooltip,
    Typography,
    Box,
    ButtonGroup,
    Button
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DataGrid } from '@mui/x-data-grid';
import { motion } from 'framer-motion';
import {
    dailyTxnHistroyUpdateApi,
    dailyTxnListApi,
    dailyTxnUploadApi,
    dailyTxnUploadHistoryApi,
    deleteDailyTxnDataApi,
    listClientApi,
    listServiceProviderApi,
    vendorTxnListApi,
    vendorTxnUploadApi
} from 'utils/API/Superdistributor APIs/SuperdistributorApiCalls';
import { globalNotifyError, globalNotifySuccess } from 'views/utilities/AlertToast';
import Spinner from 'views/Components/LoadingSinner';
import { useEffect } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import EditTransactionDialog from './TransactionEditDialog';

const UploadTransactions = () => {
    const [isVendor, setIsVendor] = useState(false);
    const [loading, setLoading] = useState({
        uploading: false,
        transactions: false,
        history: false,
        updateTransaction: false,
        fetchClients: false,
        fetchProviders: false
    });
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(100);
    const [pageTransactions, setPageTransactions] = useState(0);
    const [pageSizeTransactions, setPageSizeTransactions] = useState(100);
    const [uploadHistory, setUploadHistory] = useState([]);
    const [transactionList, setTransactionList] = useState([]);
    const [totalRows, setTotalRows] = useState({ historyCount: 0, transactionCount: 0 });
    const [openDialog, setOpenDialog] = useState(false);
    const [duplicateData, setDuplicateData] = useState({ duplcateList: [], errorList: [], isDuplicate: null });
    const [editOpen, setEditOpen] = useState({ open: false, isDelete: false, heading: '', deleteData: {} });
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [clientList, setClientList] = useState([]);
    const [providerList, setProviderList] = useState([]);

    const formatDateFunction = (inputData) => {
        const formattedData = inputData.map((item) => {
            const formattedDate = new Date(item.created_at).toLocaleString('en-IN', {
                timeZone: 'Asia/Kolkata',
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
            });
            return { ...item, formatted_date: formattedDate };
        });
        return formattedData;
    };
    const parseDuplicateEntries = (data) => {
        return data.map((item) => {
            let parsedItem = { ...item };

            if (parsedItem.duplicate_entry) {
                try {
                    parsedItem.duplicate_entry = JSON.parse(parsedItem.duplicate_entry);
                } catch (error) {
                    console.warn(`Failed to parse duplicate_entry for item with eid: ${item.eid}`, error);
                    // Optionally, keep it as is or set to null/undefined
                }
            }

            return parsedItem;
        });
    };

    const formattedHistory = formatDateFunction(uploadHistory);
    const formattedTransactions = formatDateFunction(transactionList);
    const updatedData = parseDuplicateEntries(formattedHistory);

    const uploadHistoryColumns = [
        {
            field: 'formatted_date',
            headerName: 'Date',
            minWidth: 160,
            flex: 1,
            valueGetter: (params) => params.row.formatted_date || '-'
        },
        {
            field: 'file_name',
            headerName: 'File Name',
            flex: 1,
            valueGetter: (params) => params.row.file_name || '-'
        },
        {
            field: 'txn_count',
            headerName: 'Transactions',
            flex: 1,
            valueGetter: (params) => (params.row.txn_count != null ? params.row.txn_count : '-') // check for 0 too
        },
        {
            field: 'file_size_in_bytes',
            headerName: 'File Size',
            flex: 1,
            valueGetter: (params) => (params.row.file_size_in_bytes != null ? params.row.file_size_in_bytes : '-')
        },
        {
            field: 'duplicates',
            headerName: 'Duplicates',
            flex: 1,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                const hasDuplicates = params.row.duplicate_entry && params.row.duplicate_entry.duplicates?.length > 0;

                return hasDuplicates ? (
                    <button
                        className={styles.greenSmallButton}
                        onClick={() => handleOpenDialog({ data: params.row.duplicate_entry, isDuplicate: true })}
                    >
                        Duplicates
                    </button>
                ) : null;
            }
        },
        {
            field: 'errors',
            headerName: 'Errors',
            flex: 1,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                const hasDuplicates = params.row.duplicate_entry && params.row.duplicate_entry.errors?.length > 0;

                return hasDuplicates ? (
                    <button
                        className={styles.redSmallButton}
                        onClick={() => handleOpenDialog({ data: params.row.duplicate_entry, isDuplicate: false })}
                    >
                        Errors
                    </button>
                ) : null;
            }
        }
    ];

    const transactionColumns = [
        {
            field: 'formatted_date',
            headerName: 'Date',
            flex: 1,
            minWidth: 160,
            valueGetter: (params) => params.row.formatted_date || '-'
        },
        {
            field: 'tid',
            headerName: 'Transaction ID',
            flex: 1,
            minWidth: 160,
            valueGetter: (params) => params.row.tid || '-'
        },
        {
            field: 'amount',
            headerName: 'Amount',
            flex: 1,
            minWidth: 160,
            valueGetter: (params) => (params.row.amount != null ? params.row.amount : '-') // check for 0 too
        },
        {
            field: 'provider_name',
            headerName: 'Service Provider',
            flex: 1,
            minWidth: 160,
            valueGetter: (params) => (params.row.provider_name != null ? params.row.provider_name : '-')
        },
        {
            field: 'transaction_type',
            headerName: 'Transaction Type',
            flex: 1,
            minWidth: 160,
            valueGetter: (params) => (params.row.transaction_type != null ? params.row.transaction_type : '-')
        },
        {
            field: 'rrn',
            headerName: 'RRN',
            minWidth: 160,
            flex: 1,
            valueGetter: (params) => (params.row.rrn != null ? params.row.rrn : '-')
        },
        // {
        //     field: 'card_no',
        //     headerName: 'Card No',
        //     minWidth: 160,
        //     flex: 1,
        //     valueGetter: (params) => (params.row.card_no != null ? params.row.card_no : '-')
        // },
        {
            field: 'client_name',
            headerName: 'Client Name',
            minWidth: 160,
            flex: 1,
            valueGetter: (params) => (params.row.client_name != null ? params.row.client_name : '-')
        },
        {
            field: 'actions',
            headerName: 'Edit',
            sortable: false,
            filterable: false,
            flex: 0.5,
            minWidth: 100,
            renderCell: (params) => (
                <Tooltip title="Edit Transaction">
                    <IconButton
                        onClick={() => handleEditClick(params.row)}
                        sx={{
                            backgroundColor: '#2e7d32',
                            color: '#fff',
                            '&:hover': {
                                backgroundColor: '#1b5e20'
                            },
                            borderRadius: '8px',
                            padding: '6px'
                        }}
                    >
                        <EditIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            )
        },
        {
            field: 'delete',
            headerName: 'Delete',
            sortable: false,
            filterable: false,
            flex: 0.5,
            minWidth: 100,
            renderCell: (params) => (
                <Tooltip title="Delete Transaction">
                    <IconButton
                        onClick={() => handleDeleteClick(params.row)}
                        sx={{
                            backgroundColor: '#cc0000',
                            color: '#fff',
                            '&:hover': {
                                backgroundColor: '#f44336'
                            },
                            borderRadius: '8px',
                            padding: '6px'
                        }}
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            )
        }
    ];

    const handleOpenDialog = (inputData) => {
        setDuplicateData({
            duplcateList: inputData?.data.duplicates,
            errorList: inputData?.data.errors,
            isDuplicate: inputData.isDuplicate
        });
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setDuplicateData({});
    };
    const handleEditClick = (values) => {
        setSelectedTransaction(values);
        setEditOpen({ open: true, isDelete: false, heading: 'Edit Transaction' });
    };
    const handleDeleteClick = (values) => {
        setSelectedTransaction(values.eid);
        setEditOpen({ open: true, isDelete: true, heading: 'Delete Transaction', deleteData: values });
    };
    const handleCloseEdit = () => {
        setEditOpen((prev) => ({ ...prev, open: false }));
        setSelectedTransaction(null);
    };
    const handlePageChange = (params) => {
        setPage(params.page);
        setPageSize(params?.pageSize);
    };
    const handleTransactionPageChange = (params) => {
        setPageTransactions(params.page);
        setPageSizeTransactions(params?.pageSize);
    };
    const handleFileChange = async (e) => {
        setLoading((prev) => ({ ...prev, uploading: true }));
        const file = e.target.files[0];
        if (!file) return;
        const allowedTypes = [
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'application/vnd.oasis.opendocument.spreadsheet',
            'text/csv',
            'application/csv',
            'application/vnd.ms-excel'
        ];

        const allowedExtensions = ['xlsx', 'ods', 'csv'];
        const fileExtension = file.name.split('.').pop().toLowerCase();

        if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
            globalNotifyError('Invalid file format. Please upload a .xlsx, .ods, or .csv file.');
            setLoading((prev) => ({ ...prev, uploading: false }));
            return;
        }

        const formData = new FormData();
        formData.append('file', file);

        const response = isVendor ? await vendorTxnUploadApi(formData) : await dailyTxnUploadApi(formData);
        if (response?.status === 200) {
            globalNotifySuccess('Document uploaded successfully.');
            fetchUploadHistory();
            fetchTransactionList();
        }
        setLoading((prev) => ({ ...prev, uploading: false }));
    };
    //api calls>>>
    const deleteTransaction = async (id) => {
        setLoading((prev) => ({ ...prev, updateTransaction: true }));
        const response = await deleteDailyTxnDataApi({ id, isVendor });
        if (response.status === 200) {
            globalNotifySuccess('Transaction deleted successfully');
            fetchTransactionList();
            handleCloseEdit();
        }
        setLoading((prev) => ({ ...prev, updateTransaction: false }));
    };
    const fetchUploadHistory = async () => {
        setLoading((prev) => ({ ...prev, history: true }));
        const payload = { pageNumber: page + 1, pageSize, isVendor };
        const response = await dailyTxnUploadHistoryApi(payload);
        if (response.status === 200) {
            const list = response?.data?.data;
            const rowCount = response?.data?.count;
            setTotalRows((prev) => ({ ...prev, historyCount: rowCount }));
            setUploadHistory(list);
        }
        setLoading((prev) => ({ ...prev, history: false }));
    };
    const fetchTransactionList = async () => {
        setLoading((prev) => ({ ...prev, transactions: true }));
        const payload = { pageNumber: pageTransactions + 1, pageSize: pageSizeTransactions };
        const response = isVendor ? await vendorTxnListApi(payload) : await dailyTxnListApi(payload);
        if (response.status === 200) {
            const list = response?.data?.data;
            const rowCount = response?.data?.count;
            setTotalRows((prev) => ({ ...prev, transactionCount: rowCount }));
            setTransactionList(list);
        }
        setLoading((prev) => ({ ...prev, transactions: false }));
    };
    const handleEditSubmit = async (values, actionType) => {
        setLoading((prev) => ({ ...prev, updateTransaction: true }));
        if (actionType === 'edit') {
            const updatedValues = { ...values, id: values.eid, date_of_transaction: values.date_of_txn };
            const response = await dailyTxnHistroyUpdateApi(updatedValues);
            if (response.status === 200) {
                fetchTransactionList();
                globalNotifySuccess('Changes saved successfully');
                handleCloseEdit();
            }
        } else if (actionType === 'delete') {
            deleteTransaction(selectedTransaction);
        }
        setLoading((prev) => ({ ...prev, updateTransaction: false }));
    };
    const handleListClients = async () => {
        setLoading((prev) => ({ ...prev, fetchClients: true }));
        const payload = { status: 'true' };
        const response = await listClientApi(payload);
        if (response.status === 200) {
            const list = response?.data?.data;
            setClientList(list);
        }
        setLoading((prev) => ({ ...prev, fetchClients: false }));
    };
    const handleListServiceProvider = async () => {
        setLoading((prev) => ({ ...prev, fetchProviders: true }));
        const payload = { status: 'true' };
        const response = await listServiceProviderApi(payload);
        if (response.status === 200) {
            const list = response?.data?.data;
            setProviderList(list);
        }
        setLoading((prev) => ({ ...prev, fetchProviders: false }));
    };
    useEffect(() => {
        handleListClients();
        handleListServiceProvider();
    }, []);
    useEffect(() => {
        fetchUploadHistory();
        // eslint-disable-next-line
    }, [page, pageSize, isVendor]);
    useEffect(() => {
        fetchTransactionList();
        // eslint-disable-next-line
    }, [pageTransactions, pageSizeTransactions, isVendor]);
    return (
        <>
            <motion.div
                className={styles.container}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
            >
                <Box display="flex" justifyContent="space-between" alignItems="center" className={styles.headerRow}>
                    <h2 className={styles.sectionHeading}>Transaction Upload</h2>
                    <Box>
                        <Button
                            variant="outlined"
                            size="small"
                            sx={{ marginRight: 1 }}
                            onClick={() => {
                                const clientFileUrl = localStorage.getItem('clientFileLink');
                                if (clientFileUrl) {
                                    const link = document.createElement('a');
                                    link.href = clientFileUrl;
                                    link.setAttribute('download', 'sample.ods'); // Suggested filename
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                } else {
                                    alert('Client file URL not found in local storage.');
                                }
                            }}
                        >
                            Sample Client File
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                                const vendorFileUrl = localStorage.getItem('vendorFileLink');
                                if (vendorFileUrl) {
                                    const link = document.createElement('a');
                                    link.href = vendorFileUrl;
                                    link.download = 'sample-vendor-file.xlsx';
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                } else {
                                    alert('Vendor file URL not found in local storage.');
                                }
                            }}
                        >
                            Sample Vendor File
                        </Button>
                    </Box>
                </Box>

                <Box className={styles.buttonToggleWrapper}>
                    <ButtonGroup className={styles.customButtonGroup}>
                        <Button
                            className={`${styles.customButton} ${!isVendor ? styles.customButtonActive : ''}`}
                            onClick={() => setIsVendor(false)}
                        >
                            Client Transactions
                        </Button>
                        <Button
                            className={`${styles.customButton} ${isVendor ? styles.customButtonActive : ''}`}
                            onClick={() => setIsVendor(true)}
                        >
                            Vendor Transactions
                        </Button>
                    </ButtonGroup>
                </Box>

                <motion.div
                    className={styles.uploadBox}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                >
                    <Typography variant="h6" className={styles.uploadHeading}>
                        {`Upload ${isVendor ? 'Vendor' : 'Client'} Transaction File`}
                    </Typography>
                    <Typography variant="body2" className={styles.uploadDescription}>
                        Please upload a .xlsx, .ods, or .csv file containing transaction records.
                    </Typography>
                    <input accept=".xlsx,.ods,.csv" type="file" onChange={handleFileChange} className={styles.fileInput} />
                </motion.div>

                {/* Upload History DataGrid */}
                <motion.div className={styles.tableWrapper} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}>
                    <Typography variant="h6" className={styles.tableHeading}>
                        {`${isVendor ? 'Vendor' : 'Client'} Upload History`}
                    </Typography>
                    <Paper className={styles.tableContainer}>
                        <div className={styles.dataGridScrollWrapper}>
                            <DataGrid
                                rows={updatedData}
                                columns={uploadHistoryColumns}
                                paginationMode="server"
                                pagination
                                loading={loading.history}
                                rowCount={totalRows.historyCount}
                                rowsPerPageOptions={[100, 50, 25]}
                                page={0}
                                onPaginationModelChange={(params) => handlePageChange(params)}
                                getRowId={(row) => row.eid}
                                disableRowSelectionOnClick
                                sx={{
                                    border: 'none',
                                    '& .MuiDataGrid-columnHeaders': {
                                        backgroundColor: '#f5f5f5',
                                        fontWeight: 'bold'
                                    },
                                    '& .MuiDataGrid-row:hover': {
                                        backgroundColor: '#f9f9f9'
                                    }
                                }}
                            />
                        </div>
                    </Paper>
                </motion.div>

                {/* Transactions DataGrid */}
                <motion.div className={styles.tableWrapper} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }}>
                    <Typography variant="h6" className={styles.tableHeading}>
                        {`${isVendor ? 'Vendor' : 'Client'} Transactions`}
                    </Typography>
                    <Paper className={styles.tableContainer}>
                        <div className={styles.dataGridScrollWrapper}>
                            <DataGrid
                                rows={formattedTransactions}
                                columns={transactionColumns}
                                paginationMode="server"
                                onPaginationModelChange={(params) => handleTransactionPageChange(params)}
                                pagination
                                page={0}
                                loading={loading.transactions}
                                getRowId={(row) => row.eid}
                                rowCount={totalRows.transactionCount}
                                rowsPerPageOptions={[100, 50, 25]}
                                disableRowSelectionOnClick
                                sx={{
                                    border: 'none',
                                    '& .MuiDataGrid-columnHeaders': {
                                        backgroundColor: '#f5f5f5',
                                        fontWeight: 'bold'
                                    },
                                    '& .MuiDataGrid-row:hover': {
                                        backgroundColor: '#f9f9f9'
                                    }
                                }}
                            />
                        </div>
                    </Paper>
                </motion.div>
            </motion.div>
            <div className={styles.container}>
                <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                    <DialogTitle className={styles.dialogTitle}>
                        {duplicateData.isDuplicate ? ' Duplicate Entries' : 'Error Messages'}
                        <IconButton aria-label="close" onClick={handleCloseDialog} className={styles.closeButton}>
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent>
                        <Table>
                            <TableHead>
                                {duplicateData.isDuplicate ? (
                                    <TableRow className={styles.tableHeaderRow}>
                                        <TableCell>
                                            <strong>RRN</strong>
                                        </TableCell>
                                        <TableCell>
                                            <strong>TID</strong>
                                        </TableCell>
                                    </TableRow>
                                ) : null}
                            </TableHead>
                            {duplicateData.isDuplicate ? (
                                <TableBody>
                                    {duplicateData?.duplcateList?.map((entry, index) => (
                                        <TableRow key={index}>
                                            <TableCell>{entry.rrn}</TableCell>
                                            <TableCell>{entry.tid}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            ) : (
                                <TableBody>
                                    {duplicateData?.errorList?.map((entry, index) => (
                                        <TableRow key={index}>
                                            <TableCell>{entry}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            )}
                        </Table>
                    </DialogContent>
                </Dialog>
            </div>
            <Spinner colour={'green'} loading={loading.uploading || loading.updateTransaction} />
            {selectedTransaction && (
                <EditTransactionDialog
                    open={editOpen.open}
                    onClose={handleCloseEdit}
                    initialValues={selectedTransaction}
                    clientsList={clientList}
                    providerList={providerList}
                    onSubmit={(data, actionType) => handleEditSubmit(data, actionType)}
                    heading={editOpen.heading}
                    isDelete={editOpen.isDelete}
                    deleteData={editOpen.deleteData}
                />
            )}
        </>
    );
};

export default UploadTransactions;
